import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AgentStatus } from "@/components/AgentStatus";
import { supabase } from "@/integrations/supabase/client";
import { Check, X, Clock } from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import { AuditLogDetails } from "@/types/agent";
import { AgentCoordinationPanel } from "./AgentCoordinationPanel";

type AgentType = Database['public']['Tables']['agent_types']['Row'] & {
  agent_learning: {
    performance_metrics: {
      status: string;
    } | null;
  }[] | null;
};

type AuditLog = Database['public']['Tables']['audit_logs']['Row'] & {
  details: AuditLogDetails;
};

export const AgentDashboard = () => {
  const { data: agents, isLoading: isLoadingAgents } = useQuery({
    queryKey: ['agent-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('agent_types')
        .select(`
          *,
          agent_learning (
            performance_metrics
          )
        `)
        .order('name');
      
      if (error) throw error;
      return data as AgentType[];
    },
  });

  const { data: recentActivities, isLoading: isLoadingActivities } = useQuery({
    queryKey: ['agent-activities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('action', 'AGENT_COORDINATION')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (error) throw error;
      return data as AuditLog[];
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Agent Status Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {isLoadingAgents ? (
              Array(6).fill(0).map((_, i) => (
                <Card key={i} className="p-4">
                  <div className="h-16 animate-pulse bg-gray-200 rounded" />
                </Card>
              ))
            ) : (
              agents?.map((agent) => (
                <Card key={agent.id} className="p-4">
                  <div className="space-y-2">
                    <h3 className="font-medium">{agent.name}</h3>
                    <p className="text-sm text-muted-foreground">{agent.description}</p>
                    <AgentStatus 
                      agentId={agent.id} 
                      status={agent.agent_learning?.[0]?.performance_metrics?.status} 
                    />
                  </div>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <AgentCoordinationPanel />

      <Card>
        <CardHeader>
          <CardTitle>Recent Activities</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {isLoadingActivities ? (
              Array(5).fill(0).map((_, i) => (
                <div key={i} className="h-12 animate-pulse bg-gray-200 rounded" />
              ))
            ) : (
              recentActivities?.map((activity) => (
                <div key={activity.id} className="flex items-center justify-between p-2 border rounded">
                  <div className="flex items-center gap-2">
                    {activity.details.workflow_result?.status === 'success' ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : activity.details.workflow_result?.status === 'error' ? (
                      <X className="h-4 w-4 text-red-500" />
                    ) : (
                      <Clock className="h-4 w-4 text-yellow-500" />
                    )}
                    <span>{activity.details.agent_id}</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {new Date(activity.created_at).toLocaleString()}
                  </span>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};